/**
 * ExtendedHeadingNode mirrors ExtendedParagraphNode: it adds a block-level
 * inline `style` (paragraph spacing) to the stock HeadingNode, registered via
 * the official node-replacement API under type "extended-heading". Existing
 * heading checks use the instanceof-based `$isHeadingNode`, which keeps working.
 */

import { $applyNodeReplacement } from 'lexical';
import { HeadingNode } from '@lexical/rich-text';
import { applyBlockStyle, getBlockStyleObject } from './blockStyle';

export class ExtendedHeadingNode extends HeadingNode {
    constructor(tag, style = '', key) {
        super(tag, key);
        this.__style = style;
    }

    static getType() {
        return 'extended-heading';
    }

    static clone(node) {
        return new ExtendedHeadingNode(node.__tag, node.__style, node.__key);
    }

    getStyle() {
        return this.getLatest().__style;
    }

    setStyle(style) {
        const self = this.getWritable();
        self.__style = style || '';
        return self;
    }

    createDOM(config) {
        const dom = super.createDOM(config);
        if (this.__style) {
            applyBlockStyle(dom, '', this.__style);
        }
        return dom;
    }

    updateDOM(prevNode, dom, config) {
        const needsReplace = super.updateDOM(prevNode, dom, config);
        if (prevNode.__style !== this.__style) {
            applyBlockStyle(dom, prevNode.__style || '', this.__style || '');
        }
        return needsReplace;
    }

    static importJSON(serializedNode) {
        const node = $createExtendedHeadingNode(
            serializedNode.tag,
            serializedNode.style || '',
        );
        node.setFormat(serializedNode.format);
        node.setIndent(serializedNode.indent);
        node.setDirection(serializedNode.direction);
        return node;
    }

    exportJSON() {
        return {
            ...super.exportJSON(),
            type: 'extended-heading',
            version: 1,
            style: this.__style || '',
        };
    }

    exportDOM(editor) {
        const out = super.exportDOM(editor);
        const el = out && out.element;
        if (el && el.style && this.__style) {
            const obj = getBlockStyleObject(this.__style);
            for (const [k, v] of Object.entries(obj)) {
                el.style.setProperty(k, v);
            }
        }
        return out;
    }
}

export function $createExtendedHeadingNode(tag, style = '') {
    return $applyNodeReplacement(new ExtendedHeadingNode(tag, style));
}

export function $isExtendedHeadingNode(node) {
    return node instanceof ExtendedHeadingNode;
}
